import { Form } from 'components/form'
import { useAlert } from 'contexts/alert'
import { IProjectExecutionDTO } from 'data/dtos/automation/i-project-execution-dto'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useHistory, useParams } from 'react-router-dom'
import api from 'services/api'

import { ajvResolver } from '@hookform/resolvers/ajv'
import ProjectExecutionSchema, { IProjectExecutionFormData } from 'schema/automation/project-execution'
import { useStyles } from './styles'

interface IRouteParams {
  id: string
}

const ProjectExecutionForm: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [readonly, setReadonly] = useState(false)
  const [customersA, setCustomersA] = useState([])
  const [customersB, setCustomersB] = useState([])
  const { closeAlert, openAlert } = useAlert()
  const params = useParams<IRouteParams>()
  const firstInputElement = useRef(null)
  const classes = useStyles()
  const history = useHistory()

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
    control,
    setFocus,
  } = useForm<IProjectExecutionFormData>({
    resolver: ajvResolver(ProjectExecutionSchema),
    defaultValues: {
      name: '',
      sellerId: '',
      buyerId: '',
      definitions: '',
      status: '',
      executedAt: '',
      isInactive: false,
    },
  })

  const handleChange = (formField: any) => closeAlert()

  // main data load
  useEffect(() => {
    async function loadData() {
      const { id } = params

      // form data
      await api
        .get(`/project-executions/${id}`)
        .then((response) => {
          const { data } = response.data

          const projectExecutionResult = {
            name: data.name,
            sellerId: data.sellerId.id,
            buyerId: data.buyerId.id,
            definitions: data.definitions,
            status: data.status,
            executedAt: data.executedAt,
            isInactive: data.isInactive,
          }

          return projectExecutionResult
        })
        .then((projectExecutionResult: IProjectExecutionDTO) => {
          reset(projectExecutionResult)
        })
        .catch((error) => {
          console.log(error)
          return error
        })
    }

    if (params.id) {
      loadData()
      setReadonly(true)
    }
  }, [params, params.id])

  // data save
  const onSubmit = useCallback(async (data: IProjectExecutionDTO) => {
    setIsLoading(true)

    const payLoad: IProjectExecutionDTO = {
      name: data.name,
      sellerId: data.sellerId,
      buyerId: data.buyerId,
      definitions: data.definitions,
      status: data.status,
      executedAt: data.executedAt,
      isInactive: data.isInactive,
    }

    if (params.id) {
      const { id } = params

      payLoad.id = id

      await api
        .put(`/project-executions`, payLoad)
        .then(() => {
          openAlert('Cliente atualizado com sucesso!', 'success')
          history.push('/project-executions')
        })
        .catch((error) => {
          openAlert(error?.response?.data?.data?.message ?? 'Ops! Algo inesperado aconteceu!', 'error')
        })
        .finally(() => setIsLoading(false))
    } else {
      await api
        .post('/project-executions', payLoad)
        .then(() => {
          openAlert('Dado cadastrado com sucesso!', 'success')
          reset()
        })
        .catch((error) => {
          openAlert(error?.response?.data?.data?.message ?? 'Ops! Algo inesperado aconteceu!', 'error')
        })
        .finally(() => setIsLoading(false))
    }
  }, [])

  return (
    <Form.Root onHandleSubmit={handleSubmit(onSubmit)}>
      <Form.Info
        title='Execuções de Projetos'
        subTitle='Dados coletados sobre as execuções dos projetos de automação.'
      />

      <Form.Body>
        <Form.Input
          label='Nome'
          error={!!errors.name}
          errorMessage={errors?.name?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
          maxLength={100}
          name='name'
          rules={{ onChange: (e) => handleChange(e) }}
          register={register}
        />

        <Form.Select
          label='Seller'
          error={!!errors.sellerId}
          errorMessage={errors?.sellerId?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
          name='sellerId'
          rules={{
            onChange: (e) => {
              setValue('sellerId', e.target.value)
              handleChange(e)
            },
          }}
          register={register}
          value={`${watch().sellerId}`}
          options={customersA}
        />

        <Form.Select
          label='Buyer'
          error={!!errors.buyerId}
          errorMessage={errors?.buyerId?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
          name='buyerId'
          rules={{
            onChange: (e) => {
              setValue('buyerId', e.target.value)
              handleChange(e)
            },
          }}
          register={register}
          value={`${watch().buyerId}`}
          options={customersB}
        />

        <Form.Input
          label='Definições'
          error={!!errors.definitions}
          errorMessage={errors?.definitions?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
          maxLength={4096}
          name='definitions'
          rules={{ onChange: (e) => handleChange(e) }}
          register={register}
        />

        <Form.Input
          label='Status de Execução'
          error={!!errors.status}
          errorMessage={errors?.status?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
          maxLength={100}
          name='status'
          rules={{ onChange: (e) => handleChange(e) }}
          register={register}
        />

        <Form.Input
          label='Executado em'
          error={!!errors.executedAt}
          errorMessage={errors?.executedAt?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
          name='executedAt'
          rules={{ onChange: (e) => handleChange(e) }}
          register={register}
        />

        <Form.CheckBox
          label='Inativo'
          sizing={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
          name='isInactive'
          control={control}
        />
      </Form.Body>

      <Form.Divisor />

      <Form.Footer>
        <Form.Button
          label='SALVAR'
          type='submit'
          variant='contained'
          isLoading={isLoading}
        />
        <Form.Button
          label='CANCELAR'
          type='cancel'
          variant='text'
          isLoading={isLoading}
        />
      </Form.Footer>
    </Form.Root>
  )
}

export default ProjectExecutionForm
